import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { useAsync } from 'react-use';
import {
  EmptyState,
  InfoCard,
  Link,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { relativeTime } from './relativeTime';
import { releaseDeployment } from './deploymentState';
import { ReleaseDeploymentChip } from './ReleaseDeploymentChip';
import { useDeploymentStates } from './useDeploymentStates';

type EntityRelease = {
  tagName: string;
  name?: string;
  description?: string;
  commitSha?: string;
  releasedAt: string;
  webUrl?: string;
};

export const isReleasesAvailable = (entity: Entity): boolean =>
  Boolean(entity.metadata.annotations?.['gitlab.com/project-slug']);

export const EntityReleasesCard = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const {
    kind,
    namespace = 'default',
    name,
  } = {
    kind: entity.kind,
    namespace: entity.metadata.namespace,
    name: entity.metadata.name,
  };

  // The batch route keys its answer by the ref it was given, so the same string
  // is used to ask and to look up. Lower-cased because that is the catalog's own
  // normalised form and what the releases feed stores.
  const entityRef = `${kind}:${namespace}/${name}`.toLowerCase();
  const { states } = useDeploymentStates([entityRef]);

  const { value, loading, error } = useAsync(async (): Promise<
    EntityRelease[]
  > => {
    const baseUrl = await discoveryApi.getBaseUrl('releases');
    const response = await fetch(
      `${baseUrl}/entity/${encodeURIComponent(namespace)}/${encodeURIComponent(
        kind,
      )}/${encodeURIComponent(name)}?limit=5`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => undefined);
      throw new Error(
        body?.error ??
          `Request failed with ${response.status} ${response.statusText}`,
      );
    }
    return (await response.json()).releases ?? [];
  }, [kind, namespace, name]);

  let content;
  if (loading) {
    content = <Progress />;
  } else if (error) {
    content = (
      <WarningPanel title="Could not load releases" message={error.message} />
    );
  } else if (!value || value.length === 0) {
    content = (
      <EmptyState
        title="No releases"
        missing="content"
        description="No GitLab releases have been ingested for this project yet."
      />
    );
  } else {
    content = (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Release</TableCell>
            <TableCell>Released</TableCell>
            <TableCell>Commit</TableCell>
            <TableCell>Deployment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {value.map(release => (
            <TableRow key={release.tagName}>
              <TableCell>
                <Typography variant="body2">
                  {release.webUrl ? (
                    <Link to={release.webUrl}>{release.tagName}</Link>
                  ) : (
                    release.tagName
                  )}
                  {release.name && release.name !== release.tagName
                    ? ` ${release.name}`
                    : ''}
                </Typography>
              </TableCell>
              <TableCell>
                <span title={new Date(release.releasedAt).toLocaleString()}>
                  {relativeTime(release.releasedAt)}
                </span>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="textSecondary">
                  {release.commitSha?.slice(0, 8) ?? '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <ReleaseDeploymentChip
                  deployment={releaseDeployment({
                    release,
                    state: states[entityRef],
                    releases: value,
                  })}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <InfoCard
      title="Releases"
      deepLink={{ title: 'All releases', link: '/releases' }}
    >
      {content}
    </InfoCard>
  );
};
